import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import styled from 'styled-components';
import './i18n'; // Initialize i18n

// Components
import Header from './components/Header';
import Footer from './components/Footer';

// Pages
import Home from './pages/Home';
import Tracking from './pages/Tracking';
import History from './pages/History';
import Team from './pages/Team';
// import Sponsors from './pages/Sponsors';
// import Tourists from './pages/Tourists';

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
  margin-top: 80px; /* Space for fixed header */
`;

const GlobalStyles = styled.div`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    line-height: 1.6;
    color: #1a365d;
    background: #f8fafc;
  }

  html {
    scroll-behavior: smooth;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Playfair Display', serif;
    font-weight: 300;
    line-height: 1.2;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  button {
    font-family: inherit;
  }

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #3182ce, #4299e1);
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #2c5aa0, #3182ce);
  }

  /* Leaflet map styles */
  .leaflet-container {
    height: 100%;
    width: 100%;
    border-radius: 20px;
  }

  .leaflet-popup-content-wrapper {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 15px;
    box-shadow: 0 8px 32px rgba(66, 153, 225, 0.2);
    backdrop-filter: blur(10px);
  }

  .leaflet-popup-tip {
    background: rgba(255, 255, 255, 0.95);
  }

  .leaflet-control-zoom a {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(66, 153, 225, 0.3);
    color: #3182ce;
  }

  .leaflet-control-zoom a:hover {
    background: rgba(66, 153, 225, 0.1);
    border-color: #3182ce;
  }

  /* Mobile responsive grid styles */
  @media (max-width: 768px) {
    /* Override grid layouts for mobile */
    .mobile-single-column {
      grid-template-columns: 1fr !important;
    }
    
    .mobile-padding {
      padding: 1rem !important;
    }
    
    .mobile-small-text {
      font-size: 0.9rem !important;
    }
    
    .mobile-center {
      text-align: center !important;
    }
  }

  @media (max-width: 480px) {
    /* Very small screens */
    .mobile-extra-small-text {
      font-size: 0.8rem !important;
    }
    
    .mobile-compact-padding {
      padding: 0.5rem !important;
    }
  }

  /* Animation for page transitions */
  .page-enter {
    opacity: 0;
    transform: translateY(20px);
  }

  .page-enter-active {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 300ms, transform 300ms;
  }

  .page-exit {
    opacity: 1;
  }

  .page-exit-active {
    opacity: 0;
    transform: translateY(-20px);
    transition: opacity 300ms, transform 300ms;
  }
`;

// Custom 404 Page
const NotFoundContainer = styled.div`
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #edf2f7 50%, #e2e8f0 100%);
`;

const NotFoundTitle = styled.h1`
  font-size: clamp(3rem, 8vw, 6rem);
  color: #3182ce;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #3182ce, #4299e1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const NotFoundText = styled.p`
  font-size: 1.2rem;
  color: #718096;
  margin-bottom: 2rem;
  max-width: 600px;
`;

const BackHomeButton = styled.button`
  background: linear-gradient(135deg, #3182ce, #4299e1);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(66, 153, 225, 0.3);
  }
`;

const NotFound: React.FC = () => {
  return (
    <NotFoundContainer>
      <NotFoundTitle>404</NotFoundTitle>
      <NotFoundText>
        Looks like you've sailed off course! The page you're looking for doesn't exist.
        Let's navigate you back to familiar waters.
      </NotFoundText>
      <BackHomeButton onClick={() => window.location.href = '/'}>
        Return to Home Port
      </BackHomeButton>
    </NotFoundContainer>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <GlobalStyles />
      <AppContainer>
        <Header />
        <MainContent>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/tracking" element={<Tracking />} />
            <Route path="/history" element={<History />} />
            <Route path="/team" element={<Team />} />
            {/* <Route path="/sponsors" element={<Sponsors />} /> */}
            {/* <Route path="/tourists" element={<Tourists />} /> */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </MainContent>
        <Footer />
      </AppContainer>
    </Router>
  );
};

export default App;
